import { devWarn, devError } from '../../../utils/devLog';
import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import './NewChartModal.css';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  VStack,
  HStack,
  Stack,
  Input,
  Text,
  Button,
  Box,
  Alert,
  AlertIcon,
  Radio,
  RadioGroup,
  FormControl,
  IconButton,
  Switch,
  Spinner
} from '@chakra-ui/react';
import { ViewIcon, SearchIcon } from '@chakra-ui/icons';
import { ParsedChartData} from '../../../models/types';
import { InteractiveMap, InteractiveMapRef } from './modern-ui';
import { jatakaApiService } from '../../../services/JatakaApiService';
import {
  birthDataToJatakaRequest,
  jatakaResponseToChartData,
  validateBirthData,
  logDataTransformation,
  BirthData as ApiBirthData
} from '../../../utils/apiDataTransformers';
import { locationOverrideService } from '../../../services/LocationOverrideService';
import { generateChartName } from '../../../config/dateTimeFormats';
import { getCurrentLocationWithAddress, reverseGeocode, formatRoughAddress, extractCityName, searchCityCoordinates } from '../../../services/GeocodingService';
import '../../../config/api'; // Автоматическая настройка API конфигурации
import { PrivacyMode, CoordinateFormat } from '../../../types/PrivacyTypes';
import { MaskedCoordinateInput } from './MaskedCoordinateInput';
import {
  loadPrivacySettings,
  savePrivacySettings
} from '../../../utils/privacyUtils';

interface NewChartModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCreateChart: (data: ParsedChartData, chartName: string) => void;
  onSaveChart?: (data: ParsedChartData, chartName: string) => Promise<void>;
}

interface BirthData {
  chartName: string;
  locationName: string;
  // Date fields (separate for better mobile UX)
  day: string;
  month: string;
  year: string;
  // Time fields (separate for better mobile UX)
  hour: string;
  minute: string;
  second: string;
  longitude: number; // Decimal degrees
  latitude: number; // Decimal degrees
  timezoneMode: 'auto' | 'manual';
  timezoneOffset: string; // +HH:MM format
  selectedLocation: string;
}


const NewChartModal: React.FC<NewChartModalProps> = ({ isOpen, onClose, onCreateChart, onSaveChart }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [geolocationAttempted, setGeolocationAttempted] = useState(false);

  // Store cached coordinates separately from birth data
  const [cachedCoordinates, setCachedCoordinates] = useState({ latitude: 0, longitude: 0 });

  // Privacy settings state
  const [privacyMode, setPrivacyMode] = useState<PrivacyMode>(() => loadPrivacySettings().mode);
  const [coordinateFormat, setCoordinateFormat] = useState<CoordinateFormat>(() => loadPrivacySettings().coordinateFormat);
  const [showMap, setShowMap] = useState(false); // Map hidden by default for privacy
  const [coordinatesMasked, setCoordinatesMasked] = useState(true); // Masked by default

  // Reload privacy mode from localStorage when modal opens
  useEffect(() => {
    if (isOpen) {
      const savedSettings = loadPrivacySettings();
      setPrivacyMode(savedSettings.mode);
      setCoordinateFormat(savedSettings.coordinateFormat);
    }
  }, [isOpen]);
  const [isDetectingLocation, setIsDetectingLocation] = useState(false);

  // Get showLocation setting from global/localStorage
  const showLocation = useMemo(() => {
    if (typeof window !== 'undefined' && typeof window.globalShowLocation !== 'undefined') {
      return window.globalShowLocation;
    }
    try {
      const saved = localStorage.getItem('hora-showLocation');
      return saved === null ? true : saved === 'true';
    } catch {
      return true;
    }
  }, [isOpen]); // Re-check when modal opens

  // Track if user manually edited location name
  const [isLocationNameManuallyEdited, setIsLocationNameManuallyEdited] = useState(false);

  // Track if user manually edited chart name
  const [isChartNameManuallyEdited, setIsChartNameManuallyEdited] = useState(false);

  // Track if user manually edited date/time
  const [isDateTimeManuallyEdited, setIsDateTimeManuallyEdited] = useState(false);
  
  // Ref to trigger map's auto-location function
  const mapRef = useRef<InteractiveMapRef>(null);

  // Refs for auto-focus on date/time fields
  const dayRef = useRef<HTMLInputElement>(null);
  const monthRef = useRef<HTMLInputElement>(null);
  const yearRef = useRef<HTMLInputElement>(null);
  const hourRef = useRef<HTMLInputElement>(null);
  const minuteRef = useRef<HTMLInputElement>(null);
  const secondRef = useRef<HTMLInputElement>(null);

  const [birthData, setBirthData] = useState<BirthData>({
    chartName: 'New Chart',
    locationName: '',
    day: '05',
    month: '07',
    year: '2025',
    hour: '01',
    minute: '02',
    second: '53',
    longitude: 0,
    latitude: 0,
    timezoneMode: 'auto',
    timezoneOffset: '+00:00',
    selectedLocation: ''
  });

  // Validation functions
  const getDaysInMonth = useCallback((month: number, year: number): number => {
    if (month < 1 || month > 12) return 31;
    return new Date(year, month, 0).getDate();
  }, []);

  const fieldValidation = useMemo(() => {
    const day = parseInt(birthData.day) || 0;
    const month = parseInt(birthData.month) || 0;
    const year = parseInt(birthData.year) || 0;
    const hour = parseInt(birthData.hour);
    const minute = parseInt(birthData.minute);
    const second = parseInt(birthData.second);

    const maxDays = getDaysInMonth(month, year || 2000);

    return {
      day: birthData.day.length === 2 && day >= 1 && day <= maxDays,
      month: birthData.month.length === 2 && month >= 1 && month <= 12,
      year: birthData.year.length === 4 && year >= 1 && year <= 9999,
      hour: birthData.hour.length === 2 && hour >= 0 && hour <= 23,
      minute: birthData.minute.length === 2 && minute >= 0 && minute <= 59,
      second: birthData.second.length === 2 && second >= 0 && second <= 59
    };
  }, [birthData.day, birthData.month, birthData.year, birthData.hour, birthData.minute, birthData.second, getDaysInMonth]);

  const isDateTimeValid = useMemo(() => {
    return Object.values(fieldValidation).every(Boolean);
  }, [fieldValidation]);

  // Update time and timezone when modal opens
  useEffect(() => {
    if (isOpen) {
      const now = new Date();

      // Get browser timezone offset
      const offset = now.getTimezoneOffset();
      const offsetHours = -offset / 60;
      const offsetSign = offsetHours >= 0 ? '+' : '-';
      const offsetFormatted = `${offsetSign}${String(Math.abs(Math.floor(offsetHours))).padStart(2, '0')}:${String(Math.abs((offsetHours % 1) * 60)).padStart(2, '0')}`;

      // Only update date/time if not manually edited
      if (!isDateTimeManuallyEdited) {
        // Extract date parts
        const day = String(now.getDate()).padStart(2, '0');
        const month = String(now.getMonth() + 1).padStart(2, '0');
        const year = String(now.getFullYear());

        // Extract time parts
        const hour = String(now.getHours()).padStart(2, '0');
        const minute = String(now.getMinutes()).padStart(2, '0');
        const second = String(now.getSeconds()).padStart(2, '0');

        // Generate chart name using saved format from settings
        const chartName = generateChartName(now);

        setBirthData(prev => ({
          ...prev,
          chartName: chartName,
          day,
          month,
          year,
          hour,
          minute,
          second,
          timezoneOffset: offsetFormatted,
          latitude: cachedCoordinates.latitude,
          longitude: cachedCoordinates.longitude
        }));
      } else {
        // Only update coordinates and timezone, preserve date/time
        setBirthData(prev => ({
          ...prev,
          timezoneOffset: offsetFormatted,
          latitude: cachedCoordinates.latitude,
          longitude: cachedCoordinates.longitude
        }));
      }

      // Reset manual edit flag on modal open
      setIsChartNameManuallyEdited(false);

      // Check cache and trigger geolocation if needed
      if (cachedCoordinates.latitude === 0 && cachedCoordinates.longitude === 0 && !geolocationAttempted) {
        setGeolocationAttempted(true);

        // If showLocation is false, get geolocation silently without map
        if (!showLocation) {
          getCurrentLocationWithAddress()
            .then(({ latitude, longitude }) => {
              setCachedCoordinates({ latitude, longitude });
              setBirthData(prev => ({
                ...prev,
                latitude,
                longitude,
                locationName: 'Hidden Location'
              }));
            })
            .catch((error) => {
              devWarn('Geolocation failed in hidden mode:', error.message);
              // Fallback: keep coordinates at 0,0 - button will stay disabled
            });
        } else {
          // Delay the auto-location to ensure the map is fully mounted
          setTimeout(() => {
            if (mapRef.current) {
              mapRef.current.triggerAutoLocation();
            }
          }, 100);
        }
      }
    }
  }, [isOpen, cachedCoordinates, geolocationAttempted, showLocation, isDateTimeManuallyEdited]);

  // Handle Enter key press
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Enter' && isOpen && !isLoading) {
        e.preventDefault();
        handleCreateChart();
      }
    };

    if (isOpen) {
      window.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, isLoading]);



  const handleInputChange = (field: keyof BirthData, value: string) => {
    // Track manual location name edits
    if (field === 'locationName') {
      setIsLocationNameManuallyEdited(true);
    }

    // Track manual chart name edits
    if (field === 'chartName') {
      setIsChartNameManuallyEdited(true);
    }

    setBirthData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Handler for segmented date/time inputs with auto-focus
  const handleSegmentChange = (
    field: 'day' | 'month' | 'year' | 'hour' | 'minute' | 'second',
    value: string,
    maxLength: number,
    nextRef?: React.RefObject<HTMLInputElement>
  ) => {
    // Only allow digits
    const digitsOnly = value.replace(/\D/g, '');

    // Limit to maxLength
    const limitedValue = digitsOnly.slice(0, maxLength);

    // Mark date/time as manually edited to prevent auto-updates
    setIsDateTimeManuallyEdited(true);

    setBirthData(prev => {
      const updated = { ...prev, [field]: limitedValue };

      // Update chart name if not manually edited
      if (!isChartNameManuallyEdited) {
        const year = parseInt(updated.year) || new Date().getFullYear();
        const month = parseInt(updated.month) - 1 || 0;
        const day = parseInt(updated.day) || 1;
        const hour = parseInt(updated.hour) || 0;
        const minute = parseInt(updated.minute) || 0;
        const second = parseInt(updated.second) || 0;

        // Only regenerate if we have valid date components
        if (updated.year.length === 4 && updated.month && updated.day) {
          const date = new Date(year, month, day, hour, minute, second);
          if (!isNaN(date.getTime())) {
            updated.chartName = generateChartName(date);
          }
        }
      }

      return updated;
    });

    // Auto-focus next field when current is filled
    if (limitedValue.length === maxLength && nextRef?.current) {
      nextRef.current.focus();
      nextRef.current.select();
    }
  };

  const handleLocationChange = (lat: number, lng: number, address?: string) => {
    // Update both birth data and cached coordinates
    const newCoords = { latitude: lat, longitude: lng };
    setCachedCoordinates(newCoords);

    // Check for user override first
    const override = locationOverrideService.getOverride(lat, lng);
    const locationName = override || address || '';

    setBirthData(prev => ({
      ...prev,
      latitude: lat,
      longitude: lng,
      selectedLocation: address || '',
      locationName: locationName
    }));

    // Reset manual edit flag when location changes from map
    setIsLocationNameManuallyEdited(false);
  };

  // Privacy settings handlers
  const handlePrivacyModeChange = useCallback((mode: PrivacyMode) => {
    setPrivacyMode(mode);
    savePrivacySettings({ mode });
  }, []);

  const handleCoordinateFormatChange = useCallback((format: CoordinateFormat) => {
    setCoordinateFormat(format);
    savePrivacySettings({ coordinateFormat: format });
  }, []);

  const handleCoordinatesChange = useCallback((lat: number, lng: number) => {
    setBirthData(prev => ({ ...prev, latitude: lat, longitude: lng }));
    setCachedCoordinates({ latitude: lat, longitude: lng });
  }, []);

  // Detect current location with privacy-aware address
  const handleDetectLocation = useCallback(async () => {
    setIsDetectingLocation(true);
    try {
      const { latitude, longitude } = await getCurrentLocationWithAddress();

      // Get detailed address info
      const geocodeResult = await reverseGeocode(latitude, longitude);

      if (privacyMode === 'rough') {
        // In rough mode: use city coordinates and rough address format
        const roughAddress = formatRoughAddress(geocodeResult.address);
        const cityName = extractCityName(geocodeResult.address);

        // Search for city center coordinates
        let finalLat = latitude;
        let finalLng = longitude;

        if (cityName) {
          const cityCoords = await searchCityCoordinates(
            `${cityName}, ${geocodeResult.address?.country || ''}`
          );
          if (cityCoords) {
            finalLat = cityCoords.lat;
            finalLng = cityCoords.lng;
          }
        }

        setCachedCoordinates({ latitude: finalLat, longitude: finalLng });
        setBirthData(prev => ({
          ...prev,
          latitude: finalLat,
          longitude: finalLng,
          locationName: roughAddress,
          selectedLocation: roughAddress
        }));
      } else {
        // In precise mode: use exact coordinates but still short address (city, country)
        const shortAddress = formatRoughAddress(geocodeResult.address);
        setCachedCoordinates({ latitude, longitude });
        setBirthData(prev => ({
          ...prev,
          latitude,
          longitude,
          locationName: shortAddress,
          selectedLocation: shortAddress
        }));
      }

      setIsLocationNameManuallyEdited(false);
    } catch (error) {
      devWarn('Location detection failed:', error);
      setError('Failed to detect location. Please try again or enter manually.');
    } finally {
      setIsDetectingLocation(false);
    }
  }, [privacyMode]);

  // Displayed location name - in rough mode locationName is already formatted by formatRoughAddress
  // No need to re-process it with formatAddressForPrivacy
  const displayedLocationName = birthData.locationName;

  /**
   * Расчет джатака через Jataka Calculator API
   */
  const calculateChartDataFromApi = async (): Promise<ParsedChartData> => {
    // Compose date and time strings from segmented fields
    const composedDate = `${birthData.day.padStart(2, '0')}-${birthData.month.padStart(2, '0')}-${birthData.year}`;
    const composedTime = `${birthData.hour.padStart(2, '0')}:${birthData.minute.padStart(2, '0')}:${birthData.second.padStart(2, '0')}`;

    // Create API-compatible birth data object
    const apiBirthData: ApiBirthData = {
      chartName: birthData.chartName,
      locationName: birthData.locationName,
      date: composedDate,
      time: composedTime,
      longitude: birthData.longitude,
      latitude: birthData.latitude,
      timezoneMode: birthData.timezoneMode,
      timezoneOffset: birthData.timezoneOffset,
      selectedLocation: birthData.selectedLocation
    };

    // Валидация данных перед отправкой
    const validationErrors = validateBirthData(apiBirthData);
    if (validationErrors.length > 0) {
      throw new Error(`Validation failed: ${validationErrors.join(', ')}`);
    }

    // Преобразование данных формы в API запрос
    const apiRequest = birthDataToJatakaRequest(apiBirthData);

    // Вызов API
    const apiResult = await jatakaApiService.calculateJataka(apiRequest);

    if (!apiResult.success || !apiResult.data) {
      const errorMessage = apiResult.error?.message || 'Unknown API error';
      throw new Error(`API request failed: ${errorMessage}`);
    }

    // Преобразование ответа API в данные чарта
    const chartData = jatakaResponseToChartData(apiResult.data, apiBirthData);

    // Логирование для отладки
    logDataTransformation(
      apiBirthData,
      apiRequest,
      apiResult.data,
      chartData
    );

    return chartData;
  };

  const handleCreateChart = async () => {
    setIsLoading(true);
    setError(null);

    try {
      // Save location override if user manually edited location name
      if (isLocationNameManuallyEdited && birthData.locationName.trim()) {
        locationOverrideService.setOverride(
          birthData.latitude,
          birthData.longitude,
          birthData.locationName
        );
      }

      // Расчет чарта через Jataka Calculator API
      const chartData = await calculateChartDataFromApi();

      // Debug: New Chart Created via API (production: logs disabled)

      // Debug: Calling onCreateChart (production: logs disabled)
      await onCreateChart(chartData, birthData.chartName);
      // Debug: onCreateChart completed (production: logs disabled);

      onClose(); // Close modal after successful creation

      // Focus on Basic Panchanga section after creation
      setTimeout(() => {
        const basicPanchangaSection = document.querySelector('[data-accordion-id="basic-panchanga"]') as HTMLElement;
        if (basicPanchangaSection) {
          basicPanchangaSection.focus();
          basicPanchangaSection.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        }
      }, 150);

    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error occurred';
      setError(`Failed to create chart: ${errorMessage}`);

      // Логирование ошибки для отладки
      devError('❌ Chart creation failed:', {
        error: errorMessage,
        birthData,
        timestamp: new Date().toISOString()
      });
    } finally {
      setIsLoading(false);
    }
  };


  const handleCancel = () => {
    setBirthData({
      chartName: 'New Chart',
      locationName: '',
      day: '',
      month: '',
      year: '',
      hour: '',
      minute: '',
      second: '',
      longitude: 0,
      latitude: 0,
      timezoneMode: 'auto',
      timezoneOffset: '+00:00',
      selectedLocation: ''
    });
    setError(null);
    setGeolocationAttempted(false);
    setIsChartNameManuallyEdited(false);
    onClose();
  };


  return (
    <Modal isOpen={isOpen} onClose={onClose} size={{ base: 'full', md: '2xl' }}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Create New Chart</ModalHeader>
        <ModalCloseButton />

        <ModalBody>
          <VStack spacing={4} align="stretch">
            {/* Error Display */}
            {error && (
              <Alert status="error" size="sm">
                <AlertIcon />
                <Text fontSize="sm">{error}</Text>
              </Alert>
            )}

            {/* Chart Name Input */}
            <Input
              placeholder="Chart Name"
              value={birthData.chartName}
              onChange={(e) => handleInputChange('chartName', e.target.value)}
              size="sm"
            />

            {/* Location Name Input - shows truncated in rough mode */}
            {showLocation && (
              <Input
                placeholder="Location Name"
                value={privacyMode === 'rough' ? displayedLocationName : birthData.locationName}
                onChange={(e) => handleInputChange('locationName', e.target.value)}
                size="sm"
                isReadOnly={privacyMode === 'rough'}
                bg={privacyMode === 'rough' ? 'gray.50' : 'white'}
              />
            )}

            {/* Main Content */}
            <Stack
              direction={{ base: 'column', md: 'row' }}
              spacing={4}
              align="flex-start"
              w="full"
            >
              {/* Left Column - Map with controls above */}
              <VStack spacing={2} flex="1" w="full" maxW={{ base: 'full', md: '317px' }}>
                {showLocation ? (
                  <>
                    {/* Controls row above map */}
                    <HStack spacing={2} w="full" justify="space-between" flexWrap="wrap">
                      {/* Rough/Precise Toggle */}
                      <HStack spacing={2}>
                        <Text fontSize="xs" color={privacyMode === 'rough' ? 'green.600' : 'gray.500'} fontWeight="medium">
                          Rough
                        </Text>
                        <Switch
                          size="sm"
                          isChecked={privacyMode === 'precise'}
                          onChange={(e) => handlePrivacyModeChange(e.target.checked ? 'precise' : 'rough')}
                          colorScheme="blue"
                        />
                        <Text fontSize="xs" color={privacyMode === 'precise' ? 'blue.600' : 'gray.500'} fontWeight="medium">
                          Precise
                        </Text>
                      </HStack>

                      {/* Detect Location Button */}
                      <Button
                        leftIcon={isDetectingLocation ? <Spinner size="xs" /> : <SearchIcon boxSize={3} />}
                        colorScheme="blue"
                        size="xs"
                        onClick={handleDetectLocation}
                        isLoading={isDetectingLocation}
                        loadingText="..."
                      >
                        Detect
                      </Button>

                      {/* Show/Hide Map Button */}
                      <Button
                        size="xs"
                        variant="ghost"
                        colorScheme="gray"
                        leftIcon={<ViewIcon boxSize={3} />}
                        onClick={() => setShowMap(!showMap)}
                      >
                        {showMap ? 'Hide map' : 'Show map'}
                      </Button>
                    </HStack>

                    {/* Map or placeholder */}
                    {showMap ? (
                      <Box w="full">
                        <InteractiveMap
                          ref={mapRef}
                          latitude={birthData.latitude}
                          longitude={birthData.longitude}
                          onLocationChange={handleLocationChange}
                          height="222px"
                          showSearch={true}
                          hideLocationButton={true}
                        />
                      </Box>
                    ) : (
                      <Box
                        w="full"
                        height="222px"
                        bg="gray.50"
                        borderRadius="8px"
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        border="1px solid"
                        borderColor="gray.200"
                      >
                        <Text fontSize="sm" color="gray.400">
                          Map hidden for privacy
                        </Text>
                      </Box>
                    )}
                  </>
                ) : (
                  /* Location completely hidden */
                  <Box
                    w="full"
                    height="222px"
                    bg="gray.50"
                    borderRadius="8px"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    flexDirection="column"
                    border="1px solid"
                    borderColor="gray.200"
                  >
                    <Text fontSize="2xl" color="gray.400" mb={2}>🔒</Text>
                    <Text fontSize="sm" color="gray.500" fontWeight="medium">
                      Location Hidden
                    </Text>
                    <Text fontSize="xs" color="gray.400" mt={1}>
                      Using current position
                    </Text>
                  </Box>
                )}
              </VStack>

              {/* Right Column - Form */}
              <VStack spacing={3} w="full" maxW={{ base: 'full', md: '238px' }} align="stretch">
                {/* Date Input: DD-MM-YYYY */}
                <HStack spacing={1} w="full" justify="center">
                  <FormControl isInvalid={birthData.day.length > 0 && !fieldValidation.day} w="auto">
                    <Input
                      ref={dayRef}
                      placeholder="DD"
                      value={birthData.day}
                      onChange={(e) => handleSegmentChange('day', e.target.value, 2, monthRef)}
                      onFocus={(e) => e.target.select()}
                      size="sm"
                      w="42px"
                      px="4px"
                      textAlign="center"
                      inputMode="numeric"
                      maxLength={2}
                      borderColor={birthData.day.length === 2 && !fieldValidation.day ? 'red.400' : undefined}
                    />
                  </FormControl>
                  <Text color="gray.500" fontSize="sm">-</Text>
                  <FormControl isInvalid={birthData.month.length > 0 && !fieldValidation.month} w="auto">
                    <Input
                      ref={monthRef}
                      placeholder="MM"
                      value={birthData.month}
                      onChange={(e) => handleSegmentChange('month', e.target.value, 2, yearRef)}
                      onFocus={(e) => e.target.select()}
                      size="sm"
                      w="42px"
                      px="4px"
                      textAlign="center"
                      inputMode="numeric"
                      maxLength={2}
                      borderColor={birthData.month.length === 2 && !fieldValidation.month ? 'red.400' : undefined}
                    />
                  </FormControl>
                  <Text color="gray.500" fontSize="sm">-</Text>
                  <FormControl isInvalid={birthData.year.length > 0 && !fieldValidation.year} w="auto">
                    <Input
                      ref={yearRef}
                      placeholder="YYYY"
                      value={birthData.year}
                      onChange={(e) => handleSegmentChange('year', e.target.value, 4, hourRef)}
                      onFocus={(e) => e.target.select()}
                      size="sm"
                      w="64px"
                      px="4px"
                      textAlign="center"
                      inputMode="numeric"
                      maxLength={4}
                      borderColor={birthData.year.length === 4 && !fieldValidation.year ? 'red.400' : undefined}
                    />
                  </FormControl>
                </HStack>

                {/* Time Input: HH:MM:SS */}
                <HStack spacing={1} w="full" justify="center">
                  <FormControl isInvalid={birthData.hour.length > 0 && !fieldValidation.hour} w="auto">
                    <Input
                      ref={hourRef}
                      placeholder="HH"
                      value={birthData.hour}
                      onChange={(e) => handleSegmentChange('hour', e.target.value, 2, minuteRef)}
                      onFocus={(e) => e.target.select()}
                      size="sm"
                      w="42px"
                      px="4px"
                      textAlign="center"
                      inputMode="numeric"
                      maxLength={2}
                      borderColor={birthData.hour.length === 2 && !fieldValidation.hour ? 'red.400' : undefined}
                    />
                  </FormControl>
                  <Text color="gray.500" fontSize="sm">:</Text>
                  <FormControl isInvalid={birthData.minute.length > 0 && !fieldValidation.minute} w="auto">
                    <Input
                      ref={minuteRef}
                      placeholder="MM"
                      value={birthData.minute}
                      onChange={(e) => handleSegmentChange('minute', e.target.value, 2, secondRef)}
                      onFocus={(e) => e.target.select()}
                      size="sm"
                      w="42px"
                      px="4px"
                      textAlign="center"
                      inputMode="numeric"
                      maxLength={2}
                      borderColor={birthData.minute.length === 2 && !fieldValidation.minute ? 'red.400' : undefined}
                    />
                  </FormControl>
                  <Text color="gray.500" fontSize="sm">:</Text>
                  <FormControl isInvalid={birthData.second.length > 0 && !fieldValidation.second} w="auto">
                    <Input
                      ref={secondRef}
                      placeholder="SS"
                      value={birthData.second}
                      onChange={(e) => handleSegmentChange('second', e.target.value, 2)}
                      onFocus={(e) => e.target.select()}
                      size="sm"
                      w="42px"
                      px="4px"
                      textAlign="center"
                      inputMode="numeric"
                      maxLength={2}
                      borderColor={birthData.second.length === 2 && !fieldValidation.second ? 'red.400' : undefined}
                    />
                  </FormControl>
                </HStack>

                {/* Coordinate Display with Privacy Controls */}
                {showLocation ? (
                  <MaskedCoordinateInput
                    latitude={birthData.latitude}
                    longitude={birthData.longitude}
                    onChange={handleCoordinatesChange}
                    format={coordinateFormat}
                    onFormatChange={handleCoordinateFormatChange}
                    masked={coordinatesMasked}
                    onMaskedChange={setCoordinatesMasked}
                    size="sm"
                  />
                ) : (
                  <VStack spacing={2} w="full">
                    <Input
                      value="Hidden"
                      isReadOnly
                      size="sm"
                      w="full"
                      textAlign="center"
                      color="gray.500"
                      bg="gray.50"
                    />
                    <Input
                      value="Hidden"
                      isReadOnly
                      size="sm"
                      w="full"
                      textAlign="center"
                      color="gray.500"
                      bg="gray.50"
                    />
                  </VStack>
                )}

                {/* Timezone Configuration */}
                <Box
                  w="full"
                  minHeight="120px"
                  bg="gray.50"
                  borderRadius="6px"
                  border="1px solid"
                  borderColor="gray.200"
                  p={3}
                >
                  <Text color="gray.700" fontSize="sm" fontWeight="600" mb={2}>
                    Timezone Configuration
                  </Text>
                  
                  <RadioGroup
                    value={birthData.timezoneMode}
                    onChange={(value) => handleInputChange('timezoneMode', value as 'auto' | 'manual')}
                  >
                    <VStack spacing={1} align="start">
                      <Radio value="auto" colorScheme="blue" size="sm">
                        <Text fontSize="xs">Auto-detect from browser</Text>
                      </Radio>
                      <Radio value="manual" colorScheme="blue" size="sm">
                        <Text fontSize="xs">Enter manually</Text>
                      </Radio>
                    </VStack>
                  </RadioGroup>

                  {/* Auto-detected timezone display */}
                  {birthData.timezoneMode === 'auto' && (
                    <Box
                      mt={2}
                      p={1}
                      bg="blue.50"
                      borderRadius="4px"
                    >
                      <Text color="blue.700" fontSize="xs" fontWeight="600">
                        🕒 Auto-detected from browser: UTC{birthData.timezoneOffset}
                      </Text>
                    </Box>
                  )}

                  {/* Manual timezone input */}
                  {birthData.timezoneMode === 'manual' && (
                    <Box mt={2}>
                      <Input
                        placeholder="+HH:MM"
                        value={birthData.timezoneOffset}
                        onChange={(e) => handleInputChange('timezoneOffset', e.target.value)}
                        size="xs"
                        width="100%"
                        fontSize="xs"
                      />
                    </Box>
                  )}
                </Box>
              </VStack>
            </Stack>

          </VStack>
        </ModalBody>

        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={handleCancel} disabled={isLoading}>
            Cancel
          </Button>
          <Button
            colorScheme="blue"
            onClick={handleCreateChart}
            isLoading={isLoading}
            loadingText="Calculating..."
            isDisabled={isLoading || (birthData.latitude === 0 && birthData.longitude === 0) || !isDateTimeValid}
            title={
              !isDateTimeValid
                ? 'Please enter valid date and time'
                : birthData.latitude === 0 && birthData.longitude === 0
                  ? 'Please select a location first'
                  : ''
            }
          >
            Calculate Jataka
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default NewChartModal;